import React from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Check, MessageCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useOrderList } from "@/context/OrderListContext";
import { useSite } from "@/lib/api";
import { formatPrice } from "@/lib/site";

export const MarketCard = ({ product }) => {
  const { addItem, isInList } = useOrderList();
  const { data: site } = useSite();
  const inList = isInList(product.slug);
  const detailUrl = `/market/${product.slug}`;
  const waUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba, ${product.name} hakkında bilgi almak istiyorum.`)}`
    : null;

  const handleAdd = () => {
    addItem({ ...product, kind: "market", unit: "adet" }, "1 adet");
    toast.success(`${product.name} sipariş listene eklendi`, {
      description: "Adedi listeden ayarlayabilirsin.",
    });
  };

  return (
    <div
      data-testid={`market-card-${product.slug}`}
      className="group flex flex-col border border-dashed border-bordo/25 bg-paper transition-all duration-300 hover:-translate-y-1 hover:border-bordo/50 hover:shadow-[0_20px_50px_-22px_rgba(122,26,26,0.35)]"
    >
      <Link to={detailUrl} className="relative block aspect-[5/6] overflow-hidden bg-[#120D0A]">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <span className="absolute left-0 top-4 bg-bordo px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-cream">
          {product.subcategory}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-4">
        <Link to={detailUrl}>
          <h3 className="font-serif text-lg leading-snug text-charcoal transition-colors line-clamp-2 hover:text-bordo" title={product.name}>
            {product.name}
          </h3>
        </Link>
        <p className="mt-1.5 flex-1 text-sm text-[#8C857D] line-clamp-2">{product.short_description}</p>

        {product.price != null && (
          <div className="mt-3 flex items-baseline justify-between border-t border-dashed border-bordo/20 pt-3">
            <span className="font-mono text-[11px] uppercase tracking-wider text-charcoal/55">Fiyat</span>
            <span className="flex items-baseline gap-1">
              <span data-testid={`market-price-${product.slug}`} className="font-mono text-lg font-bold text-bordo">
                {formatPrice(product.price)}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-wide text-charcoal/40">/ {product.unit || "adet"}</span>
            </span>
          </div>
        )}

        <div className="mt-3 flex flex-col gap-2">
          <button
            data-testid={`market-add-${product.slug}`}
            onClick={handleAdd}
            className={cn(
              "inline-flex min-h-[44px] items-center justify-center gap-1.5 rounded-sm px-3 py-2.5 text-sm font-medium uppercase tracking-wide transition-colors",
              inList ? "bg-charcoal text-cream" : "bg-bordo text-cream hover:bg-bordo-dark"
            )}
          >
            {inList ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
            {inList ? "Listede" : "Listeme Ekle"}
          </button>
          {waUrl && (
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`market-whatsapp-${product.slug}`}
              className="inline-flex min-h-[40px] items-center justify-center gap-1.5 rounded-sm border border-[#25D366] px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#1a7d3c] transition-colors hover:bg-[#25D366] hover:text-charcoal"
            >
              <MessageCircle className="h-3.5 w-3.5" /> WhatsApp&apos;tan Sor
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketCard;
